import { sendPost } from './dataUtilities';
import env from '../env';
import {
  AdditionalShippingRequest,
  BillingRequest,
  PaymentType,
  RegisterRequest,
  ShippingRequest,
  SubscriptionRequest,
  UserRequest,
  IntroProductRegisterRequest,
  RegisterSource,
} from './registerConnectorTypes';
import { Friend } from '../ContextProviders/friendContextTypes';
import { Country } from '../ContextProviders/appContextTypes';
import { Headers } from './dataTypes';
import { returnOnlyNumbers } from '../../components/AccountForm/utilities/accountFormUtilities';
import { getUUID, SEASONAL_PLAN_CODE } from '..';
import { Prospect } from '../../pages/GTBCustomize/ContextProviders/SharedContextTypes';

export const POST_REGISTER = '/api/2/register';
export const POST_REGISTER_V2 = '/api/2/register/social';
const POST_SEND_VERIFICATION_PHONE_CODE = '/api/1/verify-number/verify';
const POST_VERIFY_PHONE_CODE = '/api/1/verify-number/check';
const CURRENCY = 'USD';
const INTRO_PRODUCT_SOURCE = 'introProduct';
export const FFFSOCIAL_PLANCODE = 'fffsocial';
export const NON_UNIQUE_EMAIL_MESSAGE = 'The email %s has already been registered.';

const SOURCE: RegisterSource = 'publicCustomize';

export const postRegister = (registerRequest: RegisterRequest, hCaptchaToken: string) => {
  const headers: Headers = {};
  headers['hcaptcha-token'] = hCaptchaToken;
  return sendPost(
    env.registerBaseUrl + POST_REGISTER, { headers }, registerRequest,
  );
};

export const postRegisterV2 = (registerRequest: RegisterRequest, hCaptchaToken: string) => {
  const headers: Headers = {};
  headers['hcaptcha-token'] = hCaptchaToken;
  return sendPost(
    env.registerBaseUrl + POST_REGISTER_V2, { headers }, registerRequest,
  );
};

export const postIntroProductRegister = (
  registerRequest: IntroProductRegisterRequest,
  hCaptchaToken: string,
) => {
  const headers: Headers = {};
  headers['hcaptcha-token'] = hCaptchaToken;
  return sendPost(`${env.registerBaseUrl}${POST_REGISTER}`, { headers }, registerRequest);
};

function buildBillingRequest(
  payment_type: PaymentType | string,
  preferred_locale: string,
  has_gdpr: boolean,
): BillingRequest {
  return {
    payment_type,
    preferred_locale,
    has_gdpr,
  };
}

function buildShippingRequest(
  ship_country: string,
  ship_state: string,
  ship_zipcode: string,
): ShippingRequest {
  return {
    ship_country,
    ship_state: ship_state || '',
    ship_zipcode,
  };
}

function buildAdditionalShippingRequest(
  ship_first_name: string,
  ship_last_name: string,
  ship_address_1: string,
  ship_address_2: string,
  ship_city: string,
  ship_phone: string,
): AdditionalShippingRequest {
  return {
    ship_first_name,
    ship_last_name,
    ship_address_1,
    ship_address_2,
    ship_city,
    ship_phone,
  };
}

function buildSubscriptionRequest(
  currency: string,
  plan: string,
  coupon: string,
  recurly_token_id: string,
): SubscriptionRequest {
  return {
    currency,
    plan,
    coupon,
    recurly_token_id,
  };
}

function buildUserRequest(
  first_name: string,
  last_name: string,
  email: string,
  password: string,
): UserRequest {
  return {
    first_name,
    last_name,
    email,
    password,
  };
}

export function getLocaleFromCountry(countries: Country[], friendCountry: string): string {
  const currentCountry = countries.find(((c) => c.countryCode === friendCountry));
  if (currentCountry) {
    return currentCountry.labels.locale;
  }
  return 'en-US';
}

export function getGDPRStatusFromCountry(countries: Country[], friendCountry: string): boolean {
  const currentCountry = countries.find(((c) => c.countryCode === friendCountry));
  if (currentCountry) {
    return currentCountry.hasGdpr;
  }
  return false;
}

export const createRegisterRequest = (
  recurly_token: string,
  friend: Friend,
  countries: Country[],
  paymentType: PaymentType,
): RegisterRequest => {
  const {
    address, firstName, lastName, phoneNumber, password, email, inviteCode,
  } = friend;
  const sanitizedPhoneNumber = returnOnlyNumbers(phoneNumber);
  const {
    shipAddress1, shipAddress2, shipCity, shipCountry, shipState, shipZipCode,
  } = address;
  return {
    customize_requests: [],
    billing_request: buildBillingRequest(
      paymentType,
      getLocaleFromCountry(countries, shipCountry),
      getGDPRStatusFromCountry(countries, shipCountry),
    ),
    shipping_request: buildShippingRequest(
      shipCountry,
      shipState,
      shipZipCode,
    ),
    additional_shipping_request: buildAdditionalShippingRequest(
      firstName,
      lastName,
      shipAddress1,
      shipAddress2,
      shipCity,
      sanitizedPhoneNumber,
    ),
    subscription_request: buildSubscriptionRequest(CURRENCY, FFFSOCIAL_PLANCODE, '', recurly_token),
    user_request: buildUserRequest(firstName, lastName, email, password),
    campaign_id: 0,
    invite_code: inviteCode,
    uuid: getUUID(),
    source: SOURCE,
  };
};

export const createIntroProductRegisterRequest = (
  recurly_token: string,
  prospect: Prospect,
  countries: Country[],
  paymentType: string,
  subscription_items: string,
  cartId: number,
): IntroProductRegisterRequest => {
  const {
    address, firstName, lastName, password, email,
  } = prospect;
  const {
    shipAddress1, shipAddress2, shipCity, shipCountry, shipState, shipZipCode,
  } = address;
  return {
    subscription_items: [subscription_items],
    customize_requests: [],
    billing_request: buildBillingRequest(
      paymentType,
      getLocaleFromCountry(countries, shipCountry),
      getGDPRStatusFromCountry(countries, shipCountry),
    ),
    shipping_request: buildShippingRequest(shipCountry, shipState, shipZipCode),
    additional_shipping_request: {
      ship_first_name: firstName,
      ship_last_name: lastName,
      ship_address_1: shipAddress1,
      ship_address_2: shipAddress2,
      ship_city: shipCity,
    },
    subscription_request: buildSubscriptionRequest(CURRENCY, SEASONAL_PLAN_CODE, '', recurly_token),
    user_request: buildUserRequest(firstName, lastName, email, password),
    campaign_id: 0,
    invite_code: '',
    source: INTRO_PRODUCT_SOURCE,
    cart_id: cartId,
  };
};

export const createGTBCheckoutRegisterRequest = (
  recurly_token: string,
  prospect: Prospect,
  countries: Country[],
  paymentType: string,
  planCode: string,
): RegisterRequest => {
  const {
    address, firstName, lastName, phoneNumber, password, email,
  } = prospect;
  const sanitizedPhoneNumber = returnOnlyNumbers(phoneNumber);
  const {
    shipAddress1, shipAddress2, shipCity, shipCountry, shipState, shipZipCode,
  } = address;
  return {
    customize_requests: [],
    billing_request: buildBillingRequest(
      paymentType,
      getLocaleFromCountry(countries, shipCountry),
      getGDPRStatusFromCountry(countries, shipCountry),
    ),
    shipping_request: buildShippingRequest(
      shipCountry,
      shipState,
      shipZipCode,
    ),
    additional_shipping_request: buildAdditionalShippingRequest(
      firstName,
      lastName,
      shipAddress1,
      shipAddress2,
      shipCity,
      sanitizedPhoneNumber,
    ),
    subscription_request: buildSubscriptionRequest(CURRENCY, planCode, '', recurly_token),
    user_request: buildUserRequest(firstName, lastName, email, password),
    campaign_id: 0,
    invite_code: '',
    uuid: getUUID(),
    source: SOURCE,
  };
};

export const postSendVerificationPhoneCode = (phoneNumber: string) => sendPost(
  env.registerBaseUrl + POST_SEND_VERIFICATION_PHONE_CODE,
  {},
  { to_number: `+${returnOnlyNumbers(phoneNumber)}` },
);
export const postVerifyPhoneCode = (phoneNumber: string, code: string) => sendPost(
  env.registerBaseUrl + POST_VERIFY_PHONE_CODE,
  {},
  { to_number: `+${returnOnlyNumbers(phoneNumber)}`, code },
);
